<template>
  <div>
    <popup-hint hint="quickbooks">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="QuickBooks"/>
      </template>
      <template v-slot:top>
        By integrating with QuickBooks online, your general ledger is kept up-to-date
        with all production, inventory, sales and purchasing done in PURTRAK.
      </template>
    </popup-hint>

    <v-card flat>
      <v-tabs :vertical="!$vuetify.breakpoint.mobile" show-arrows v-if="!init" v-model="tab">
        <v-tab>Connection</v-tab>
        <v-tab-item>
          <v-card flat height="calc(100vh - 106px)" class="overflow-y">
            <v-container>
              <template v-if="!accountingIntegrationProvider">
                <v-row>
                  <v-col/>
                  <v-col cols="12" sm="10" align="center">
                    To connect, you will be taken to {{providerName}} and asked to Sign In if you are not already signed in.
                    You will then have to confirm you want to make the connection before being returned to PŪRTRAK to
                    continue setting up the integration.
                  </v-col>
                  <v-col/>
                </v-row>
                <v-row class="my-4">
                  <v-col/>
                  <v-col align="center">
                    <v-hover v-slot:default="{ hover }">
                      <img :src="hover?qboButtonHover:qboButtonNormal" @click="requestAuthorize('QBO')"  style="max-width:150px;display:inline-block;vertical-align:middle"/>
                    </v-hover>
                  </v-col>
                  <v-col/>
                </v-row>
                <v-row>
                  <v-col/>
                  <v-col cols="12" sm="10" class="text-body-2" style="line-height:25px">
                    <ul>
                      <li>All transactions you do within PŪRTRAK, including inventory, production, sales, and purchasing,
                        will automatically update {{providerName}} as appropriate. Therefore, this should be your
                        primary way of working.</li>
                      <li><strong>Do not use inventory (i.e. item, products, spirits and services) directly in {{providerName}} as changes
                        will not be reflected in PŪRTRAK.</strong></li>
                      <li>
                        Within {{providerName}}, make sure to turn off inventory tracking, since that will now be handled by PŪRTRAK:
                            <ol>
                              <li>Navigate to <strong>Settings > Your Company > Accounts and Settings</strong>, then click the <strong>Sales</strong> tab.</li>
                              <li>In <strong>Products and Services</strong>, ensure <strong>Track quantity and price/rate</strong> and <strong>Track inventory quantity on hand</strong> are turned <strong>Off</strong>.</li>
                              <li><strong>Save</strong> your changes.</li>
                              <li><strong>Any inventory currently in {{providerName}} should be deleted, i.e. made inactive, and then re-entered in PŪRTRAK.</strong></li>
                            </ol>
                      </li>
                    </ul>
                  </v-col>
                  <v-col/>
                </v-row>
              </template>
              <template v-else>
                <v-row>
                  <v-col align="center"><v-img src="@/assets/QB_64x64.png" width="64"/></v-col>
                </v-row>
                <v-row class="mt-10">
                  <v-col align="center">
                    PŪRTRAK is connected to QuickBooks Online company <strong>{{accountingIntegrationProvider.companyName}}</strong>
                  </v-col>
                </v-row>

                <v-row class="mt-10 text-body-2" v-if="loadingAccounts">
                  <v-col cols="8" offset="2" align="center">
                    Getting accounts information ...
                  </v-col>
                </v-row>
                <v-row class="mt-10 text-body-2" v-if="loadingAccounts">
                  <v-col align="center">
                    <v-progress-circular color="primary" indeterminate size="50"/>
                  </v-col>
                </v-row>

<!--
                <v-row class="mt-10 text-body-2" v-if="!synced">
                  <v-col cols="8" offset="2" align="center">
                    You must synchronize PŪRTRAK and QuickBooks Online.
                    <v-btn text color="primary" @click="tab=1">Go to sync</v-btn>
                  </v-col>
                </v-row>
-->
                <v-row class="mt-10" v-if="accountingIntegrationProvider.problem">
                  <v-col align="center"><v-btn color="primary" outlined @click="requestAuthorize('QBO')">Reconnect</v-btn></v-col>
                </v-row>
                <v-row class="mt-10" v-if="!loadingAccounts">
                  <v-col align="center"><v-btn color="primary" outlined @click="confirmDisconnect=true">Disconnect</v-btn></v-col>
                </v-row>
              </template>
            </v-container>
          </v-card>
        </v-tab-item>

        <v-tab :disabled="!accountingIntegrationProvider">Accounts</v-tab>
        <v-tab-item>
          <apps-qbo-accounts ref="accounts"/>
        </v-tab-item>

        <v-tab :disabled="!accountingIntegrationProvider">Synchronize</v-tab>
        <v-tab-item>
          <v-card flat height="calc(100vh - 130px)" class="overflow-y ml-6 mt-6">
            <div v-if="synced" class="mb-6">
              Last synchronized {{accountingIntegrationProvider.lastSync | formatDate}}.
              <div class="text-caption mt-4 mr-12">
                It should not be necessary to synchronize again, since all inventory, sales and expense activity performed in
                PŪRTRAK is automatically sent to {{providerName}}.
              </div>
            </div>
            <v-btn @click="sync" color="primary" :loading="loading">Sync now</v-btn>
            <v-container class="text-body-1">
              <template v-for="(syncItem,index) in syncItems">
                <apps-qbo-sync-item :key="index" :value="syncItem.value" :text="syncItem.text"/>
              </template>
            </v-container>
          </v-card>
        </v-tab-item>

        <v-tab @click="clearAlerts()" :disabled="!accountingIntegrationProvider">
          <v-badge color="red" dot :value="showAlert">
            Log
          </v-badge>
        </v-tab>
        <v-tab-item :eager="!!accountingIntegrationProvider">
          <apps-qbo-log ref="log"/>
        </v-tab-item>
      </v-tabs>
    </v-card>

    <v-dialog v-model="confirmDisconnect" persistent max-width="350">
      <v-card>
        <v-card-title>Disconnect</v-card-title>
        <v-divider/>
        <v-card-text class="pt-4">
          Are you sure you want to disconnect the connection to <strong>{{providerName}}</strong>?
          <div class="mt-4">
            If you disconnect, all account information is removed from PŪRTRAK and
            inventory tracking will no longer be done.
          </div>
        </v-card-text>
        <v-divider/>
        <v-card-actions class="pr-6">
          <v-spacer></v-spacer>
          <v-btn color="primary" plain @click="disconnect" :loading="loading">Disconnect</v-btn>
          <v-btn color="primary" @click="confirmDisconnect=false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <confirm-leave v-model="next"/>
  </div>
</template>

<style scoped>
.v-stepper__header {
  box-shadow: none;
}
.v-stepper__items {
  box-shadow: none;
}
.v-stepper {
  box-shadow: none !important;
}
.v-stepper__content {
  padding-bottom:0px;
}
</style>

<script>
import AppsQboSyncItem from '@/views/apps/qbo/AppsQboSyncItem'
import AppsQboAccounts from '@/views/apps/qbo/AppsQboAccounts'
import AppsQboLog from '@/views/apps/qbo/AppsQboLog'
import confirmLeave from '@/mixins/confirmLeave'

export default {
  name: 'AppsQbo',
  mixins: [confirmLeave],
  components: {AppsQboSyncItem,AppsQboAccounts,AppsQboLog},
  props: {
    alert: {type:Boolean, required:false, default:false}
  },
  data() {
    return {
      tab: 0,
      loadingAccounts: false,
      showAlert: this.alert,
      accountingIntegrationProvider: null,  // lastSync
      loading: false,

      syncItems: [{value:null,text:'Currencies',step:'currencies'},{value:null,text:'Taxes',step:'taxes'},{value:null,text:'Terms',step:'terms'},
                  {value:null,text:'Payment methods',step:'paymentMethods'},{value:null,text:'Accounts',step:'accounts'},{value:null,text:'Customers',step:'customers'},
                  {value:null,text:'Suppliers',step:'vendors'},{value:null,text:'Items',step:'items'},{value:null,text:'Spirits',step:'spirits'},
                  {value:null,text:'Wine',step:'wines'},{value:null,text:'Non-excise alcohol',step:'nonExcises'},{value:null,text:'Products',step:'products'},
                  {value:null,text:'Bundles',step:'bundles'},{value:null,text:'Fermenters',step:'fermenters'},
                  {value:null,text:'Tanks',step:'tanks'},{value:null,text:'Casks',step:'casks'},{value:null,text:'Bulk Containers',step:'bulkContainers'}],
      init: true,
      type: 'Currency',
      confirmDisconnect: false
    }
  },
  computed: {
    synced() {
      return this.accountingIntegrationProvider != null &&
             this.accountingIntegrationProvider.lastSync != null;
    },
    providerName() {
      return 'QuickBooks Online';
    },
    qboButtonNormal() {
      return require('@/assets/C2QB_green_btn_med_default.png');
    },
    qboButtonHover() {
      return require('@/assets/C2QB_green_btn_med_hover.png');
    }
  },
  async mounted() {
    if (!this.$store.getters.company.allowAccounting)
      this.$store.commit('setError', {message:'Your plan does not allow connecting to QuickBooks Online', type:'warning'})

    // Callback from accounting authorization
    if (this.$route.query.error || this.$route.query.state) {
      let error = this.$route.query.error;
      // Must re-load company!
      if (localStorage.getItem('oauthCompanyId')) {
        this.$store.commit('setCompanyId', localStorage.getItem('oauthCompanyId'));
        localStorage.removeItem('oauthCompanyId');
        await this.loadCompany();
      }
      else if (!error)
        error = 'Distillery not found';
      if (error === 'access_denied')
        error = 'Access to accounting system denied';
      else if (error)
        error = 'Unable to connect to accounting system'

      const state = this.$route.query.state;
      if (!error && state) {
        if (localStorage.getItem('oauthState') !== state)
          error = 'Unable to connect to accounting system';
        else
          await this.authorize(localStorage.getItem('oauthProvider'), this.$route.query.code, this.$route.query.realmId);
      }
      if (error)
        this.$store.commit('setError', {type:'error',message:error});

      localStorage.removeItem('oauthProvider');
      localStorage.removeItem('oauthState');

      this.$router.replace({query: {}});
    }

    else if (this.$store.state.companyId)
      await this.loadCompany();

    this.init = false;
  },

  methods: {
    async loadCompany() {
      const results = await this.$apiCall(`query AppsQbo {
                                                accountingIntegrationProvider {
                                                  name,companyName,lastSync,problem
                                                }
                                              }`,
                                          {});
      if (!results.error) {
        this.accountingIntegrationProvider = results.data.accountingIntegrationProvider;
        if (this.synced) {
          for (const syncItem of this.syncItems)
            syncItem.value = 1;
        }
      }
    },
    async requestAuthorize(provider) {
      localStorage.setItem('oauthProvider', provider);
      localStorage.setItem('oauthState', Date.now());
      localStorage.setItem('oauthCompanyId', this.$store.state.companyId);
      const results = await this.$apiCall(`query AppsQbo($provider:String!,$state:String!) {
                                                accountingIntegrationProviderAuthUri(provider:$provider,state:$state)
                                              }`,
                                          {provider:provider,state:localStorage.getItem('oauthState')});
      if (!results.error)
        location.href = results.data.accountingIntegrationProviderAuthUri;
    },
    async authorize(provider, code, realmId) {
      const results = await this.$apiCall(`mutation AppsQbo($input:AccountingIntegrationAuthorizeInput!) {
                                                accountingIntegrationAuthorize(input:$input) {
                                                  companyId,name,lastSync
                                                }
                                              }`,
                                          {input:{provider,code,realmId}});
      if (!results.error) {
        await this.reloadCompanies();
        this.$store.commit('setCompanyId', results.data.accountingIntegrationAuthorize.companyId);

        setTimeout(async () => {
          this.loadingAccounts = true;
          const accountsSyncResults = await this.$apiCall(`mutation AppsQbo {accountingIntegrationSync(step:"accounts init") {name}}`, {});
          this.loadingAccounts = false;
          if (!accountsSyncResults.error)
            this.tab = 1;
        }, 1000);

        return true;
      }
      return false;
    },
    async reloadCompanies() {
      const error  = await this.$store.dispatch('refreshCompanies');
      if (!error)
        this.loadCompany();
    },
    async disconnect() {
      this.loading = true;
      const results = await this.$apiCall(`mutation AppsQbo {
                                                accountingIntegrationRevoke {name}
                                              }`,
                                          {});
      this.loading = false;
      if (!results.error) {
        this.confirmDisconnect = false;
        setTimeout(() => this.reloadCompanies(), 120000);   // 2 minutes to clear server cache
        this.accountingIntegrationProvider = null;
      }
    },
    async sync() {
      for (const syncItem of this.syncItems)
        syncItem.value = null;

      this.loading = true;
      for (const syncItem of this.syncItems) {
        syncItem.value = 0;
        const results = await this.$apiCall(`mutation AppsQbo($step: String!) {accountingIntegrationSync(step:$step) {name}}`, {step:syncItem.step});
        if (results.error) {
          syncItem.value = null;
          this.loading = false;
          return;
        }
        syncItem.value = 1;
      }

      let results = await this.$apiCall(`mutation AppsQbo {accountingIntegrationSync(step:"complete") {name,companyName,lastSync,problem}}`, {});
      this.loading = false;
      if (results.error)
        return;

      this.accountingIntegrationProvider = results.data.accountingIntegrationSync;
      await this.reloadCompanies();

      // Warn if no taxes defined
      const taxes = [];
      await this.$lazyListLoad(taxes, 'taxes2', 'ref', 100, [{field:'active', comparator:'eq', value:'true'}]);
      if (taxes.length === 0)
        this.$store.commit('setError', {message:'No sales taxes were found. Make sure taxes are setup in QuickBooks and synced with PŪRTRAK.', type:'warning'});

    },
    async clearAlerts() {
      if (!this.alert)
        return;

      await this.$apiCall(`mutation AppsQbo {setAlertForAddOn(name:"QuickBooks",on:false)}`, {});
      await this.$store.dispatch('checkTasks', true);
      this.showAlert = false;
    }
  }
}
</script>